import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * NEXORA Ocean Design System (NODS)
 * ThemeProvider Component
 * 
 * Manages theme state (light/dark/system) using localStorage persistence
 * and system preference detection.
 * 
 * Based on next-themes logic but adapted for React CRA
 */

const ThemeContext = createContext({
  theme: 'dark',
  setTheme: () => {},
  systemTheme: 'dark'
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export default function ThemeProvider({ children, defaultTheme = 'dark', storageKey = 'nexora-theme' }) {
  const [theme, setThemeState] = useState(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    }
    return defaultTheme;
  });

  const [systemTheme, setSystemTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'dark';
  });

  // Listen to system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const activeTheme = theme === 'system' ? systemTheme : theme;

    root.classList.remove('light', 'dark');
    root.classList.add(activeTheme);
    
    // Store preference
    localStorage.setItem(storageKey, theme);
  }, [theme, systemTheme, storageKey]);

  const setTheme = (newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark' || newTheme === 'system') {
      setThemeState(newTheme);
    }
  };

  const value = {
    theme,
    setTheme,
    systemTheme,
    resolvedTheme: theme === 'system' ? systemTheme : theme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
